import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { fetchAddressByCep, AddressData } from '../utils/viaCepApi';

export interface SignupFormData {
    // Step 1: Basic Info
    name: string;
    email: string;
    phone: string;
    cpf: string;

    // Step 2: Address
    cep: string;
    street: string;
    number: string;
    complement: string;
    neighborhood: string;
    city: string;
    state: string;

    // Step 3: Segment
    segment: string;

    // Step 4: Business Size
    businessSize: 'single' | 'franchise';

    // Step 5: Professional Count
    professionalCount: string;

    // Step 6: Establishment Name
    establishmentName: string;

    // Step 7: Password
    password: string;
    confirmPassword: string;
}

const INITIAL_FORM_DATA: SignupFormData = {
    name: '',
    email: '',
    phone: '',
    cpf: '',
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    segment: '',
    businessSize: 'single',
    professionalCount: '',
    establishmentName: '',
    password: '',
    confirmPassword: ''
};

export const SEGMENTS = [
    { id: 'salon', label: 'Salão de Beleza', icon: 'content_cut' },
    { id: 'barbershop', label: 'Barbearia', icon: 'face' },
    { id: 'nail_salon', label: 'Esmalteria', icon: 'spa' },
    { id: 'aesthetics', label: 'Centro de Estética', icon: 'self_improvement' },
    { id: 'podology', label: 'Podologia', icon: 'footprint' },
    { id: 'massotherapy', label: 'Massoterapia', icon: 'spa' },
    { id: 'spa', label: 'Spa', icon: 'hot_tub' },
    { id: 'other', label: 'Outro segmento', icon: 'store' }
];

export const PROFESSIONAL_COUNTS = [
    { id: '1', label: '1 profissional' },
    { id: '2', label: '2 profissionais' },
    { id: '3-4', label: '3 a 4 profissionais' },
    { id: '5-10', label: '5 a 10 profissionais' },
    { id: '11-30', label: '11 a 30 profissionais' },
    { id: '30+', label: 'Mais de 30 profissionais' }
];

export const TOTAL_STEPS = 7;

export function useSignupWizard() {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState<SignupFormData>(INITIAL_FORM_DATA);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoadingCep, setIsLoadingCep] = useState(false);

    const updateFormData = useCallback((updates: Partial<SignupFormData>) => {
        setFormData(prev => ({ ...prev, ...updates }));
    }, []);

    const nextStep = useCallback(() => {
        if (currentStep < TOTAL_STEPS) {
            setCurrentStep(prev => prev + 1);
            setError(null);
        }
    }, [currentStep]);

    const prevStep = useCallback(() => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
            setError(null);
        }
    }, [currentStep]);

    const goToStep = useCallback((step: number) => {
        if (step >= 1 && step <= TOTAL_STEPS) {
            setCurrentStep(step);
            setError(null);
        }
    }, []);

    // Busca endereço pelo CEP
    const searchCep = useCallback(async (cep: string) => {
        setIsLoadingCep(true);
        setError(null);

        const address = await fetchAddressByCep(cep);

        if (address) {
            updateFormData({
                cep: address.cep,
                street: address.street,
                neighborhood: address.neighborhood,
                city: address.city,
                state: address.state
            });
        } else {
            setError('CEP não encontrado. Verifique e tente novamente.');
        }

        setIsLoadingCep(false);
    }, [updateFormData]);

    // Validações por passo
    const validateStep = useCallback((step: number): boolean => {
        // Admin Bypass Check
        const adminEmail = import.meta.env.VITE_ADMIN_EMAIL;
        const isAdmin = adminEmail && formData.email?.trim().toLowerCase() === adminEmail.toLowerCase();

        switch (step) {
            case 1: // Basic Info
                if (!formData.name.trim()) {
                    setError('Nome é obrigatório');
                    return false;
                }
                if (!formData.email.trim()) {
                    setError('Email é obrigatório');
                    return false;
                }
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                    setError('Email inválido');
                    return false;
                }
                if (!formData.phone.trim()) {
                    setError('Telefone é obrigatório');
                    return false;
                }
                if (!formData.cpf.trim()) {
                    setError('Documento (CPF ou CNPJ) é obrigatório');
                    return false;
                }
                const cleanedDoc = formData.cpf.replace(/\D/g, '');
                if (cleanedDoc.length !== 11 && cleanedDoc.length !== 14) {
                    setError('Documento inválido (Mínimo 11 ou 14 dígitos)');
                    return false;
                }
                return true;

            case 2: // Address
                if (isAdmin) return true; // Skip for admin
                if (!formData.cep.trim()) {
                    setError('CEP é obrigatório');
                    return false;
                }
                if (!formData.number.trim()) {
                    setError('Número é obrigatório');
                    return false;
                }
                return true;

            case 3: // Segment
                if (isAdmin) return true; // Skip for admin
                if (!formData.segment) {
                    setError('Selecione o segmento do seu negócio');
                    return false;
                }
                return true;

            case 4: // Business Size
                if (isAdmin) return true; // Skip for admin
                if (!formData.businessSize) {
                    setError('Selecione o tamanho do seu negócio');
                    return false;
                }
                return true;

            case 5: // Professional Count
                if (isAdmin) return true; // Skip for admin
                if (!formData.professionalCount) {
                    setError('Selecione a quantidade de profissionais');
                    return false;
                }
                return true;

            case 6: // Establishment Name
                if (isAdmin) return true; // Skip for admin
                if (!formData.establishmentName.trim()) {
                    setError('Nome do estabelecimento é obrigatório');
                    return false;
                }
                return true;

            case 7: // Password
                if (!formData.password) {
                    setError('Senha é obrigatória');
                    return false;
                }
                if (formData.password.length < 6) {
                    setError('Senha deve ter pelo menos 6 caracteres');
                    return false;
                }
                if (formData.password !== formData.confirmPassword) {
                    setError('As senhas não coincidem');
                    return false;
                }
                return true;

            default:
                return true;
        }
    }, [formData]);

    // Avançar com validação
    const handleNext = useCallback(() => {
        if (validateStep(currentStep)) {
            // Admin Fast Track: If step 1 is valid and user is admin, jump to password step
            if (currentStep === 1) {
                const adminEmail = import.meta.env.VITE_ADMIN_EMAIL;
                if (adminEmail && formData.email?.trim().toLowerCase() === adminEmail.toLowerCase()) {
                    console.log('[Signup] Admin Detected - Fast tracking to password setup');
                    setCurrentStep(7);
                    return;
                }
            }

            nextStep();
        }
    }, [currentStep, validateStep, nextStep, formData.email]);

    // Submissão final
    const handleSubmit = useCallback(async () => {
        if (!validateStep(currentStep)) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // 1. Criar usuário via Supabase Auth
            console.log('[Signup] Criando usuário...');
            const { data: authData, error: authError } = await supabase.auth.signUp({
                email: formData.email,
                password: formData.password,
                options: {
                    data: {
                        full_name: formData.name,
                        phone: formData.phone,
                        document: formData.cpf.replace(/\D/g, '')
                    }
                }
            });

            if (authError) {
                console.error('[Signup] Erro no Auth:', authError);
                throw authError;
            }
            if (!authData.user) throw new Error('Usuário não foi criado.');

            console.log('[Signup] Usuário criado:', authData.user.id);

            // Check if admin bypass
            const adminEmail = import.meta.env.VITE_ADMIN_EMAIL;
            const isAdmin = adminEmail && formData.email?.trim().toLowerCase() === adminEmail.toLowerCase();

            if (isAdmin) {
                console.log('[Signup] Admin Signup - Skipping Tenant Creation');
                await supabase.auth.signOut();
                alert('Conta de Administrador criada com sucesso! Faça login para acessar o painel.');
                navigate('/login');
                return;
            }

            // 2. Criar Tenant usando função RPC (bypassa RLS)
            const slug = formData.establishmentName
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, '')
                + '-' + Math.floor(Math.random() * 999);

            console.log('[Signup] Criando tenant via RPC...');
            const { data: tenantId, error: tenantError } = await supabase
                .rpc('create_tenant_for_new_user', {
                    p_user_id: authData.user.id,
                    p_name: formData.establishmentName,
                    p_phone: formData.phone,
                    p_slug: slug,
                    p_segment: formData.segment || null,
                    p_business_size: formData.businessSize,
                    p_professional_count: formData.professionalCount || null,
                    p_address_cep: formData.cep.replace(/\D/g, '') || null,
                    p_address_street: formData.street || null,
                    p_address_number: formData.number || null,
                    p_address_complement: formData.complement || null,
                    p_address_neighborhood: formData.neighborhood || null,
                    p_address_city: formData.city || null,
                    p_address_state: formData.state || null,
                    p_cnpj: formData.cpf.replace(/\D/g, '').length === 14 ? formData.cpf.replace(/\D/g, '') : null,
                    p_cpf: formData.cpf.replace(/\D/g, '').length === 11 ? formData.cpf.replace(/\D/g, '') : null
                });

            if (tenantError) {
                console.error('[Signup] Erro ao criar tenant:', tenantError);
                throw new Error(`Erro ao criar estabelecimento: ${tenantError.message}`);
            }

            console.log('[Signup] Tenant criado:', tenantId);

            // 3. Fazer logout para evitar conflito com TenantContext
            // O usuário precisa confirmar o email antes de logar de qualquer forma
            console.log('[Signup] Fazendo logout para evitar race condition...');
            await supabase.auth.signOut();

            alert('Conta criada com sucesso! Verifique seu email para confirmar e depois faça login.');
            navigate('/login');

        } catch (err: any) {
            console.error('[Signup] Erro:', err);
            setError(err.message || 'Erro ao criar conta. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }, [currentStep, formData, navigate, validateStep]);

    return {
        currentStep,
        totalSteps: TOTAL_STEPS,
        formData,
        loading,
        error,
        isLoadingCep,
        updateFormData,
        nextStep: handleNext,
        prevStep,
        goToStep,
        searchCep,
        handleSubmit,
        setError
    };
}
